
import React, { ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { AuthContext } from './AuthContext';
import { AuthContextType } from '@/types/auth';
import { useAuthState } from '@/hooks/useAuthState';
import { signIn as serviceSignIn } from '@/services/authService';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { session, user, profile, loading } = useAuthState();
  const { toast } = useToast();

  // Helper functions to check user roles
  const isAdministrator = () => {
    return profile?.role === 'administrator' || profile?.role === 'admin';
  };

  const isOrganizationUser = () => {
    return profile?.role === 'organization_user' || profile?.role === 'organization';
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await serviceSignIn({ email, password });
      
      if (error) {
        console.error('Sign in error:', error.message);
        return { error };
      } else {
        console.log('Sign in successful');
        return { error: null };
      }
    } catch (error: any) {
      console.error('Unexpected sign in error:', error);
      return { error };
    }
  };

  const signUp = async (
    email: string, 
    password: string, 
    userData: {
      first_name: string;
      last_name: string;
      gender?: string;
      organization_id?: string;
      contact_number1?: string;
      contact_number2?: string;
      role?: 'admin' | 'organization' | 'public' | 'administrator' | 'organization_user';
    }
  ) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role || 'public',
            gender: userData.gender,
            organization_id: userData.organization_id,
            contact_number1: userData.contact_number1,
            contact_number2: userData.contact_number2,
          },
        },
      });

      if (error) {
        console.error('Sign up error:', error.message);
        toast({
          variant: "destructive",
          title: "Sign up failed",
          description: error.message,
        });
      } else {
        console.log('Sign up successful');
        toast({
          title: "Account created",
          description: "Please check your email for verification.",
        });
      }

      return { error };
    } catch (error: any) {
      console.error('Unexpected sign up error:', error);
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: "An unexpected error occurred.",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Clear session from Supabase
      await supabase.auth.signOut({ scope: 'local' });
      
      // Manually clear all localStorage items related to Supabase auth
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      console.log('Sign out successful');
      
      // Force a hard reload to clear all state
      window.location.replace('/');
    } catch (error) {
      console.error('Sign out error:', error);
      
      // Even if there's an error, force clear everything
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      window.location.replace('/');
    }
  };

  // Create the context value object with all auth-related state and functions
  const contextValue: AuthContextType = {
    session,
    user,
    profile,
    loading,
    isAdministrator,
    isOrganizationUser,
    signIn,
    signUp,
    signOut,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
